import { computed, ref, type Ref } from 'vue';
import { useElementMeasurements } from './useElementMeasurements';

export interface AdvMenuItem {
  id: string | number;
  label?: string;
  icon?: string | null;
  activeIcon?: string | null;
  bgColor?: string | null;
  textColor?: string | null;
  indicatorColor?: string | null;
  mode?: 'outline' | 'filled' | 'flat';
  textBlink?: boolean;
  animation?: 'gradient' | 'pulse' | 'shimmer' | 'breathe' | 'glow';
  hidden?: boolean;
  url?: string | null;
}

export interface ProcessedItem extends Omit<AdvMenuItem, 'id'> {
  id: string | number;
  active: boolean;
  hasCustomBg: boolean;
  isHidden: boolean;
  _cacheVersion?: number;
  _measured?: boolean;
}

export function useProcessedItems(
  items: Ref<AdvMenuItem[]>,
  internalActiveId: Ref<string | number | null>,
  uniformStyle: Ref<boolean>,
  measurementCache: ReturnType<typeof useElementMeasurements> | null
) {
  const previousProcessedItems = ref<ProcessedItem[]>([]);
  let processedItemsCacheVersion = 0;

  const processedItems = computed<ProcessedItem[]>(() => {
    const newItems: ProcessedItem[] = [];

    const activeIdChanged = previousProcessedItems.value.length > 0 &&
      previousProcessedItems.value.some(item =>
        (item.active && item.id !== internalActiveId.value) ||
        (!item.active && item.id === internalActiveId.value)
      );

    const uniformStyleChanged = previousProcessedItems.value.length > 0 &&
      previousProcessedItems.value.length === items.value.length &&
      previousProcessedItems.value.some((item, index) => {
        const sourceItem = items.value[index];
        if (!sourceItem) return false;
        const itemMode = sourceItem.mode || (sourceItem.bgColor ? 'filled' : 'flat');
        const expectedHasCustomBg = uniformStyle.value ? false : (itemMode === 'filled' || itemMode === 'outline');
        return item.hasCustomBg !== expectedHasCustomBg;
      });

    const itemsLengthChanged = previousProcessedItems.value.length !== items.value.length;

    for (let index = 0; index < items.value.length; index++) {
      const item = items.value[index]!;
      const previousItem = previousProcessedItems.value[index];

      let needsRecalculation = false;

      if (itemsLengthChanged || !previousItem) {
        needsRecalculation = true;
      } else if (previousItem.id !== item.id) {
        needsRecalculation = true;
      } else if (activeIdChanged && (item.id === internalActiveId.value || previousItem.active)) {
        needsRecalculation = true;
      } else if (uniformStyleChanged) {
        needsRecalculation = true;
      } else if (
        item.label !== previousItem.label ||
        item.icon !== previousItem.icon ||
        item.activeIcon !== previousItem.activeIcon ||
        item.bgColor !== previousItem.bgColor ||
        item.textColor !== previousItem.textColor ||
        item.mode !== previousItem.mode ||
        item.hidden !== previousItem.hidden ||
        item.url !== previousItem.url
      ) {
        needsRecalculation = true;
      }

      if (needsRecalculation) {
        const active = internalActiveId.value === item.id;
        const itemMode = item.mode || 'flat';
        const hasCustomBg = uniformStyle.value ? false : (itemMode === 'filled' || itemMode === 'outline');
        const isHidden = item.hidden === true;
        const measured = measurementCache?.cache.value.items.has(item.id) ?? false;

        newItems.push({
          id: item.id,
          label: item.label,
          icon: item.icon,
          activeIcon: item.activeIcon,
          bgColor: item.bgColor,
          textColor: item.textColor,
          indicatorColor: item.indicatorColor,
          mode: item.mode,
          textBlink: item.textBlink,
          animation: item.animation,
          hidden: item.hidden,
          url: item.url,
          active,
          hasCustomBg,
          isHidden,
          _cacheVersion: processedItemsCacheVersion,
          _measured: measured
        });
      } else if (previousItem) {
        const measured = measurementCache?.cache.value.items.has(item.id) ?? false;
        newItems.push({
          id: previousItem.id,
          label: previousItem.label,
          icon: previousItem.icon,
          activeIcon: previousItem.activeIcon,
          bgColor: previousItem.bgColor,
          textColor: previousItem.textColor,
          indicatorColor: previousItem.indicatorColor,
          mode: previousItem.mode,
          textBlink: previousItem.textBlink,
          animation: previousItem.animation,
          hidden: previousItem.hidden,
          url: previousItem.url,
          active: previousItem.active,
          hasCustomBg: previousItem.hasCustomBg,
          isHidden: previousItem.isHidden,
          _cacheVersion: previousItem._cacheVersion,
          _measured: measured
        });
      } else {
        const active = internalActiveId.value === item.id;
        const itemMode = item.mode || 'flat';
        const hasCustomBg = uniformStyle.value ? false : (itemMode === 'filled' || itemMode === 'outline');
        const isHidden = item.hidden === true;
        const measured = measurementCache?.cache.value.items.has(item.id) ?? false;

        newItems.push({
          id: item.id,
          label: item.label,
          icon: item.icon,
          activeIcon: item.activeIcon,
          bgColor: item.bgColor,
          textColor: item.textColor,
          indicatorColor: item.indicatorColor,
          mode: item.mode,
          textBlink: item.textBlink,
          animation: item.animation,
          hidden: item.hidden,
          url: item.url,
          active,
          hasCustomBg,
          isHidden,
          _cacheVersion: processedItemsCacheVersion,
          _measured: measured
        });
      }
    }

    previousProcessedItems.value = newItems;
    return newItems;
  });

  const invalidateCache = () => {
    processedItemsCacheVersion++;
  };

  return {
    processedItems,
    invalidateCache
  };
}
